// pages/alerts/AlertDetailModal.jsx — SOUND-847 row drill-down.
//
// Stat tiles, daily series (GET /alerts/series), programs + last received,
// refund outcomes, and — for a merchant — its MIDs. Chrome follows the house
// portal modals (components/Mids/MidImportModal).
import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, ShieldAlert, ShieldCheck, CreditCard, RotateCcw } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import PageKpis from "components/PageKpis";
import { useAlertSeries } from "hooks/useAlerts";
import {
  PROGRAMS,
  PROGRAM_LABELS,
  formatDay,
  formatRate,
  rowKind,
  rowLabel,
  lastAlertDate,
  refundOutcomes,
  orderNotFoundWarning,
  chargebackDays,
  sortRows,
  BRANDS,
  brandTileHint,
  unknownBrandNote,
  brandTone,
  UNGRADED_BRAND_NOTE,
} from "utilities/alertCoverage";
import {
  RatePill,
  PROGRAM_COLORS,
  Swatch,
  LinkBadge,
  AgoText,
  SectionHeading,
} from "./alertsUi";

const fmtInt = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));

// Same token reader the risk and analytics charts use, so the chart chrome
// follows the light/dark theme.
function cssVar(name, alpha = 1) {
  if (typeof window === "undefined") return "";
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return "";
  const [h, s, l] = raw.split(/\s+/);
  return `hsla(${parseFloat(h)}, ${s}, ${l}, ${alpha})`;
}

// `brandKey` picks the tone rule: Amex/Discover are never graded (brandTone).
function rateKpi(label, value, hint, icon, brandKey) {
  const tone = brandTone(brandKey, value);
  return {
    label,
    value: value == null ? "—" : value,
    format: value == null ? "text" : "percent",
    tone: tone === "muted" ? "neutral" : tone,
    hint,
    icon,
  };
}

function DailyBars({ data, series, height }) {
  const grid = cssVar("--border", 0.5);
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -12 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
          <XAxis dataKey="date" tickFormatter={formatDay} tick={{ fontSize: 11 }} minTickGap={24} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={44} />
          <Tooltip
            labelFormatter={formatDay}
            formatter={(v, name) => [fmtInt(v), name]}
            contentStyle={{
              background: cssVar("--card", 0.98),
              border: `1px solid ${cssVar("--border", 1)}`,
              borderRadius: 8,
              fontSize: 12,
            }}
            cursor={{ fill: cssVar("--muted", 0.5) }}
          />
          {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
          {series.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              stackId="a"
              fill={s.color}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ChartPanel({ title, loading, error, children }) {
  return (
    <div className="surface rounded-xl p-4 ring-1 ring-border">
      <SectionHeading>{title}</SectionHeading>
      {loading ? (
        <div className="h-[120px] animate-pulse rounded bg-muted" />
      ) : error ? (
        <p className="text-sm text-destructive">Failed to load the daily series.</p>
      ) : (
        children
      )}
    </div>
  );
}

function Identity({ row }) {
  const kind = rowKind(row);
  const merchant = row.disputifierMerchantName || row.disputifierMerchantId;
  if (kind !== "mid") {
    return <p className="mt-0.5 text-sm text-muted-foreground">Disputifier merchant · {rowLabel(row).subtitle}</p>;
  }
  const crm = row.crm;
  const dName = row.disputifierMidName || row.disputifierMidId;
  return (
    <div className="mt-0.5 space-y-0.5 text-sm text-muted-foreground">
      {crm ? (
        <p>
          CRM: {[crm.companyName, crm.midNumber && `MID ${crm.midNumber}`, crm.descriptor, crm.processorName]
            .filter(Boolean)
            .join(" · ")}
        </p>
      ) : (
        <p>Not linked to a CRM MID</p>
      )}
      <p>Disputifier: {[dName, !crm && row.descriptor, merchant].filter(Boolean).join(" · ")}</p>
    </div>
  );
}

export default function AlertDetailModal({ row, from, to, asOf, midRows = [], midLoading, onClose }) {
  const { days, loading, error } = useAlertSeries({ key: row.key, from, to });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const kind = rowKind(row);
  const counts = row.counts || {};
  const byBrand = row.byBrand || {};
  const cov = row.coverage || {};
  const outcomes = refundOutcomes(counts);
  const cbDays = useMemo(() => chargebackDays(days), [days]);
  const merchantMids = useMemo(
    () =>
      kind === "merchant"
        ? sortRows(
            midRows.filter((m) => m.disputifierMerchantId === row.disputifierMerchantId),
            "-alerts"
          )
        : [],
    [kind, midRows, row.disputifierMerchantId]
  );

  const unknownNote = unknownBrandNote(byBrand);

  const outcomeBars = [
    { key: "refunded", label: "Refunded", value: outcomes.refunded, cls: "bg-success" },
    { key: "already", label: "Already refunded", value: outcomes.alreadyRefunded, cls: "bg-info" },
    {
      key: "notFound",
      label: "Order not found or no CRM/gateway access",
      value: outcomes.orderNotFound,
      cls: "bg-destructive",
    },
    {
      key: "other",
      label: "Other not refunded (declined, overlap, threshold)",
      value: outcomes.other,
      cls: "bg-muted-foreground/30",
    },
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="surface flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl shadow-lg ring-1 ring-border"
        role="dialog"
        aria-modal="true"
        aria-labelledby="alert-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border p-5">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {kind === "merchant" ? "Merchant" : kind === "unassigned" ? "Merchant · no MID" : "MID"} ·{" "}
              {formatDay(from)} – {formatDay(to)}
            </p>
            <h3 id="alert-detail-title" className="mt-1 flex items-center text-lg font-semibold">
              <span className="truncate">{rowLabel(row).title}</span>
              <LinkBadge row={row} />
            </h3>
            <Identity row={row} />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost h-8 w-8 shrink-0 p-0"
            aria-label="Close"
            autoFocus
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          <div>
            <PageKpis
              columns={{ base: 2, md: 3, lg: 6 }}
              items={[
                rateKpi(
                  "Coverage",
                  cov.total,
                  `${fmtInt(counts.chargebacksUnmatched)} CBs without alert`,
                  <ShieldCheck />
                ),
                ...BRANDS.map((b) =>
                  rateKpi(b.label, cov[b.key], brandTileHint(byBrand[b.key], cov[b.key]), <CreditCard />, b.key)
                ),
                rateKpi("Refund rate", row.refundRate, `of ${fmtInt(counts.alerts)} alerts`, <RotateCcw />),
              ]}
            />
            <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
              <p>{UNGRADED_BRAND_NOTE}</p>
              {unknownNote && <p>{unknownNote}</p>}
            </div>
          </div>

          <ChartPanel title="Alerts per day by program" loading={loading} error={error}>
            <DailyBars
              data={days}
              height={200}
              series={["ethoca", "cdrn", "rdr"].map((p) => ({
                key: p,
                label: PROGRAM_LABELS[p],
                color: PROGRAM_COLORS[p],
              }))}
            />
          </ChartPanel>

          <div className="grid gap-4 md:grid-cols-2">
            <ChartPanel title="TC40 notices per day" loading={loading} error={error}>
              <DailyBars
                data={days}
                height={140}
                series={[{ key: "tc40", label: "TC40", color: PROGRAM_COLORS.tc40 }]}
              />
            </ChartPanel>
            <ChartPanel title="Chargebacks per day" loading={loading} error={error}>
              <DailyBars
                data={cbDays}
                height={140}
                series={[
                  { key: "unmatched", label: "No alert", color: cssVar("--destructive", 0.85) },
                  { key: "matched", label: "Matched to an alert", color: cssVar("--muted-foreground", 0.35) },
                ]}
              />
            </ChartPanel>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="surface rounded-xl p-4 ring-1 ring-border">
              <SectionHeading>Programs</SectionHeading>
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="py-1 text-left font-semibold">Program</th>
                    <th className="py-1 text-right font-semibold">In range</th>
                    <th className="py-1 text-right font-semibold">Last received</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {PROGRAMS.map((p) => {
                    const last = row.lastReceived?.[p];
                    return (
                      <tr key={p}>
                        <td className="py-1.5">
                          <Swatch color={PROGRAM_COLORS[p]} />
                          {PROGRAM_LABELS[p]}
                        </td>
                        <td className="py-1.5 text-right tabular-nums">{fmtInt(counts[p])}</td>
                        <td className="py-1.5 text-right">
                          {last ? (
                            <>
                              <span className="text-muted-foreground">{formatDay(last)} · </span>
                              <AgoText date={last} asOf={asOf} />
                            </>
                          ) : (
                            <span className="text-muted-foreground">Never received</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="mt-2 text-[11px] text-muted-foreground">
                TC40 is a fraud notice, so it is excluded from coverage and refund rate.
              </p>
            </div>

            <div className="surface rounded-xl p-4 ring-1 ring-border">
              <SectionHeading>Refund outcomes</SectionHeading>
              {outcomes.alerts > 0 && (
                <div className="mb-3 flex h-2.5 overflow-hidden rounded-full bg-muted">
                  {outcomeBars.map((b) =>
                    b.value ? (
                      <span
                        key={b.key}
                        className={b.cls}
                        style={{ width: `${(b.value / outcomes.alerts) * 100}%` }}
                        title={`${b.label}: ${fmtInt(b.value)}`}
                      />
                    ) : null
                  )}
                </div>
              )}
              <table className="w-full text-[13px]">
                <tbody className="divide-y divide-border">
                  {outcomeBars.map((b) => (
                    <tr key={b.key}>
                      <td className="py-1.5">
                        <span className={`mr-1.5 inline-block h-2 w-2 rounded-sm align-middle ${b.cls}`} />
                        {b.label}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{fmtInt(b.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {orderNotFoundWarning(counts) && (
                <p className="mt-2 flex items-start gap-1.5 text-xs text-warning">
                  <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  More than 5% of alerts couldn't find the order. Check the CRM and gateway connections
                  before flagging to Disputifier.
                </p>
              )}
            </div>
          </div>

          {kind === "merchant" && (
            <div className="surface rounded-xl p-4 ring-1 ring-border">
              <SectionHeading>MIDs</SectionHeading>
              {midLoading ? (
                <div className="h-16 animate-pulse rounded bg-muted" />
              ) : merchantMids.length === 0 ? (
                <p className="text-sm text-muted-foreground">No MID rows for this merchant in range.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                        <th className="py-1 text-left font-semibold">MID</th>
                        <th className="py-1 text-right font-semibold">Coverage</th>
                        <th className="py-1 text-right font-semibold">Alerts</th>
                        <th className="py-1 text-right font-semibold">Refund</th>
                        <th className="py-1 text-right font-semibold">Last alert</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {merchantMids.map((m) => (
                        <tr key={m.key}>
                          <td className="py-1.5">
                            <span className="inline-flex items-center">
                              {rowLabel(m).title}
                              <LinkBadge row={m} />
                            </span>
                          </td>
                          <td className="py-1.5 text-right">
                            <RatePill value={m.coverage?.total} />
                          </td>
                          <td className="py-1.5 text-right tabular-nums">{fmtInt(m.counts?.alerts)}</td>
                          <td className="py-1.5 text-right">
                            <RatePill value={m.refundRate} />
                          </td>
                          <td className="py-1.5 text-right">
                            <AgoText date={lastAlertDate(m)} asOf={asOf} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    MID coverage is a lower bound: about 40% of chargebacks cannot be placed on a MID, so
                    they count only at merchant level. Coverage {formatRate(cov.total)} above is complete.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
